import React, { Fragment, useMemo } from 'react';
import {
  Card,
  Grid,
  Badge,
  BadgeProps,
  Heading,
} from '@printdeal/ui-library';
import { getCauses, getStatuses } from '@printdeal/business-logic';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'gatsby';
import { useGetOrderQuery } from '@printdeal/checkout-frontend';
import {
  messages as complaintMessages,
  MessagesProp as complaintMessagesProp,
} from '../../../translations/complaint';
import { complaintLineItemNames } from '../../../../../helpers/complaints-helper';
import { Complaint } from '../../../../../types/complaints';
import { colorToSemanticVariant } from '../../../../../helpers/brand';

interface ComplaintListItemProps {
  complaint: Complaint
}

const ComplaintListItem = ({ complaint }: ComplaintListItemProps) => {
  const intl = useIntl();
  const { complaintId, status, createdAt } = complaint;
  const causes = Object.values(getCauses());
  const categories = [...complaint.categories];
  // @ts-expect-error
  const { badgeColor } = getStatuses().find(
    (complaintStatus) => complaintStatus.mcpComplaintsKey === status,
  );
  const complaintStateMessage = complaintMessages[status];
  const { data } = useGetOrderQuery({ orderNumber: complaint.merchantOrderId });
  const order = data?.message;

  // @ts-expect-error type mismatch
  const lineItems = complaintLineItemNames(order?.lineItems, complaint?.items);

  const mapCauses = useMemo(() => (causes
    .filter((cause) => categories
      .find((category) => category === cause.mappedMcpKey))
    .map((cause) => cause.translationKey.split('.')[2])
  ), [categories]);

  return (
    <Card
      variant="horizontal"
      link={<Link to={complaintId} />}
      title=""
    >
      <Grid className="pd-mb-2 pd-grid-cols-2 pd-gap-2 md:pd-grid-cols-[3fr,2fr,5fr,2fr]">
        <Grid.Item as={Heading} size="3">
          <FormattedMessage id="complaints.list.complaintNumberPrefix" defaultMessage="Complaint" />
          {complaintId}
        </Grid.Item>
        <Grid.Item>
          <Badge variant={colorToSemanticVariant(badgeColor) as BadgeProps['variant']}>
            {intl.formatMessage(complaintStateMessage)}
          </Badge>
        </Grid.Item>
        <Grid.Item as="p" className="pd-text-dim-500">
          {causes && mapCauses
            .map((category) => intl.formatMessage(complaintMessages[category as keyof complaintMessagesProp])).join(', ')}
        </Grid.Item>
        <Grid.Item>
          <time dateTime={createdAt as unknown as string} className="pd-text-wrap">
            <FormattedDate value={createdAt} year="numeric" month="long" day="numeric" />
          </time>
        </Grid.Item>
      </Grid>
      <Grid className="pd-grid-cols-2 pd-gap-2 md:pd-grid-cols-[1fr,3fr]">
        <Grid.Item as="p">
          <FormattedMessage id="complaints.list.order" defaultMessage="Order" />
          {` ${complaint.merchantOrderId}`}
        </Grid.Item>
        <Grid.Item as="p" className="pd-truncate pd-text-dim-500">
          {lineItems.map((lineItem, i) => (
            <Fragment key={`${lineItem.name}${lineItem.quantity}`}>
              {lineItem.name} <FormattedMessage
                id="account.order.orderline.quantity"
                defaultMessage="{quantity} {quantity, plural, one {piece} other {pieces}}"
                values={{ quantity: lineItem.quantity }}
              /> {(lineItems.length > 1 && i !== (lineItems.length - 1)) && ' | '}
            </Fragment>
          ))}
        </Grid.Item>
      </Grid>
    </Card>
  );
};

export default ComplaintListItem;
